import { Placement } from '@floating-ui/react';
import clsx from 'clsx';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, ButtonProps } from '../button/Button';
import { useFlowChartPanZoom } from '../flow-chart/useFlowChartPanZoom';
import type { GraphIconVariant } from '../graph-icon/GraphIcon';
import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import { EdgeTooltipLayerProps } from './EdgeTooltipLayer';
import styles from './NetworkGraph.module.scss';
import { NetworkGraphViewport } from './network-graph-viewport/NetworkGraphViewport';
import { buildGraphGeometry } from './utils/buildGraphGeometry';
import { computeFitViewport } from './utils/computeFitViewport';
import {
  NETWORK_GRAPH_HEIGHT,
  NETWORK_GRAPH_WIDTH,
  type NetworkGraphLayoutEdge,
  type NetworkGraphLayoutNode,
  type NetworkGraphNodeSize,
  type NetworkGraphSector,
  resolvePrimaryNode,
} from './utils/computeRadialLayout';
import { nodeOuterRadius } from './utils/nodeGeometry';
import {
  createLayoutState,
  type NetworkGraphLayoutInputs,
  reconcileLayoutState,
} from './utils/reconcileLayoutState';
import { resolveGraphLabels } from './utils/resolveGraphLabels';

export type NetworkGraphVariant = GraphIconVariant;
export type NetworkGraphEdgeEmphasis = 'default' | 'soft' | 'selected';
export type NetworkGraphNodeState = 'default' | 'active';

export interface NetworkGraphNodeModel extends NetworkGraphLayoutNode {
  label: string;
  labelVisible: boolean;
  variant: NetworkGraphVariant;
  state: NetworkGraphNodeState;
  size: NetworkGraphNodeSize;
  activeContent?: ReactNode;
  groupSector?: NetworkGraphSector;
  isLoading?: boolean;
  isInteractive?: boolean;
  // Filtered-out nodes are neither painted nor laid out, so visible ring slots
  // stay contiguous. A node that was visible before keeps its cached position
  // when shown again; one revealed for the first time is placed incrementally
  // around the pinned layout.
  hidden?: boolean;
}

export interface NetworkGraphEdgeModel extends NetworkGraphLayoutEdge {
  id: string;
  label?: string;
  labelVisible: boolean;
  emphasis: NetworkGraphEdgeEmphasis;
  twoWayArrow?: boolean;
  tooltip?: ReactNode;
  // Filtered-out edge kept in the model but not painted (see node.hidden).
  hidden?: boolean;
}

interface NetworkGraphLocales {
  centerOnSelected: string;
  zoomIn: string;
  zoomOut: string;
}

export interface NetworkGraphProps
  extends Pick<EdgeTooltipLayerProps, 'renderEdgeTooltip'>,
    Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  nodes: readonly NetworkGraphNodeModel[];
  edges: readonly NetworkGraphEdgeModel[];
  expandedNodeIds?: readonly string[];
  // Layout anchor kept stable across interactions: the starting node stays
  // centered even after it shrinks from primary to a default node.
  primaryNodeId?: string;
  // Hover mirrored from outside the canvas (e.g. a hovered connection card):
  // renders that node hovered, reveals its name and the label of the edge
  // linking it to the focus.
  hoveredNodeId?: string | null;
  locales?: Partial<NetworkGraphLocales>;
  onNodeClick?: (nodeId: string) => void;
  onNodeHover?: (nodeId: string | null) => void;
  onEdgeClick?: (edgeId: string) => void;
  onEdgeHover?: (edgeId: string | null) => void;
  renderEdgeTooltip?: (content: ReactNode, placement: Placement) => ReactNode;
}

// Stable default so an unset prop doesn't invalidate the layout memo each render.
const EMPTY_EXPANDED_NODE_IDS: readonly string[] = [];

const DEFAULT_LOCALES: NetworkGraphLocales = {
  centerOnSelected: 'Bring selected node to view',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
};

export const NetworkGraph = ({
  nodes,
  edges,
  expandedNodeIds = EMPTY_EXPANDED_NODE_IDS,
  primaryNodeId,
  hoveredNodeId,
  locales,
  onNodeClick,
  onNodeHover,
  onEdgeClick,
  onEdgeHover,
  renderEdgeTooltip,
  className,
  'aria-label': ariaLabel,
  ...divProps
}: NetworkGraphProps) => {
  const resolvedLocales = { ...DEFAULT_LOCALES, ...locales };
  const rootRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // Pan/zoom mutates this group's transform imperatively so pointer-move /
  // wheel frames do not reconcile every node and edge through React.
  const viewportGroupRef = useRef<SVGGElement>(null);
  // The graph fills its container: the viewBox tracks the measured pixel size
  // so a near-square page canvas and a 3:2 Storybook frame both render without
  // letterboxing, and layout radii scale to whatever space is available.
  const [size, setSize] = useState({
    width: NETWORK_GRAPH_WIDTH,
    height: NETWORK_GRAPH_HEIGHT,
  });
  const [hasMeasured, setHasMeasured] = useState(false);

  // Hidden members are neither painted nor laid out: rendering, geometry,
  // label collision, AND the layout below use only the visible-filtered
  // arrays. Laying out hidden nodes would let them claim ring slots (gaps
  // where nothing is painted) and inflate the chord-fit ring radius.
  const visibleNodes = useMemo(
    () => nodes.filter(node => !node.hidden),
    [nodes]
  );
  const visibleEdges = useMemo(
    () => edges.filter(edge => !edge.hidden),
    [edges]
  );
  // The primary stays in the layout inputs even while filtered out, so the
  // layout never silently recenters on an arbitrary first visible node.
  const primaryNode = resolvePrimaryNode(nodes, primaryNodeId);
  const layoutNodes =
    primaryNode?.hidden === true
      ? [primaryNode, ...visibleNodes]
      : visibleNodes;

  // Layout is derived, historical state: reconcile it against the current inputs
  // each render. Adjusting state during render (React's supported pattern) keeps
  // positions in sync without an effect; an unchanged result skips the setState.
  const layoutInputs: NetworkGraphLayoutInputs = {
    layoutNodes,
    layoutEdges: visibleEdges,
    expandedNodeIds,
    size,
    measured: hasMeasured,
    primaryNodeId,
  };
  const [layoutState, setLayoutState] = useState(() =>
    createLayoutState(layoutInputs)
  );
  const nextLayoutState = reconcileLayoutState(layoutState, layoutInputs);
  if (nextLayoutState !== layoutState) {
    setLayoutState(nextLayoutState);
  }

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setHasMeasured(true);
        const next = { width: Math.round(width), height: Math.round(height) };
        // Bail out when the rounded size is unchanged: ResizeObserver can fire
        // on sub-pixel/reflow noise, and a fresh object would needlessly reset
        // the viewport (below) and recompute the layout.
        setSize(current =>
          current.width === next.width && current.height === next.height
            ? current
            : next
        );
      }
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  const positions = nextLayoutState.positions;

  // Geometry and label collision are the expensive downstream passes. Memoize
  // them so pan/zoom and hover do not recompute the whole graph every frame.
  const geometry = useMemo(
    () => buildGraphGeometry(visibleNodes, visibleEdges, positions),
    [visibleNodes, visibleEdges, positions]
  );
  const {
    nodeById,
    connectedPositions,
    edgeLengths,
    targetAngleOffsets,
    laneOffsets,
  } = geometry;
  const rayAngles = nextLayoutState.rayAngles;
  const { offsets: labelOffsets, boxes: labelBoxes } = useMemo(
    () =>
      resolveGraphLabels({
        nodes: visibleNodes,
        edges: visibleEdges,
        positions,
        rayAngles,
        geometry,
      }),
    [visibleNodes, visibleEdges, positions, rayAngles, geometry]
  );
  // Resolve endpoints once, then paint all strokes before all labels so a
  // crossing edge can't cover another edge's label (SVG paints in DOM order).
  const renderableEdges = useMemo(() => {
    const orderedEdges = [...visibleEdges].sort(
      (first, second) =>
        // Selected last so it paints above a reciprocal lane twin.
        Number(first.emphasis === 'selected') -
          Number(second.emphasis === 'selected') ||
        (edgeLengths.get(second.id) ?? 0) - (edgeLengths.get(first.id) ?? 0) ||
        first.id.localeCompare(second.id)
    );

    return orderedEdges.flatMap(edge => {
      const source = nodeById.get(edge.sourceId);
      const target = nodeById.get(edge.targetId);
      const sourcePosition = positions[edge.sourceId];
      const targetPosition = positions[edge.targetId];
      if (!source || !target || !sourcePosition || !targetPosition) {
        return [];
      }

      return [{ edge, source, target, sourcePosition, targetPosition }];
    });
  }, [visibleEdges, edgeLengths, nodeById, positions]);
  const primaryPosition = primaryNode ? positions[primaryNode.id] : undefined;
  const fitViewport = useMemo(
    () =>
      computeFitViewport(
        visibleNodes.flatMap(node => {
          const position = positions[node.id];

          return position
            ? [{ ...position, radius: nodeOuterRadius(node.size) }]
            : [];
        }),
        size,
        {
          boxes: labelBoxes,
          center: primaryPosition ?? {
            x: size.width / 2,
            y: size.height / 2,
          },
        }
      ),
    [visibleNodes, positions, size, labelBoxes, primaryPosition]
  );
  const {
    setViewport,
    isPanning,
    suppressAfterDrag,
    zoomAtCenter,
    panZoomHandlers,
  } = useFlowChartPanZoom({
    svgRef,
    viewportGroupRef,
    graphWidth: size.width,
    graphHeight: size.height,
    minScale: Math.min(0.5, fitViewport.scale),
    maxScale: 3,
    wheelZooms: true,
  });
  // Apply the fit when the view context changes: first real measurement,
  // container resize, or a new primary (fresh layout).
  // a resize still drops the user's pan/zoom (now to a fitted view);
  // re-anchoring the prior view point is the upgrade path if ever wanted.
  const fitKey = `${size.width}x${size.height}:${hasMeasured}:${primaryNodeId}`;
  const lastFitKeyRef = useRef(fitKey);
  useLayoutEffect(() => {
    if (lastFitKeyRef.current === fitKey) {
      return;
    }
    lastFitKeyRef.current = fitKey;
    setViewport(fitViewport);
  }, [fitKey, fitViewport, setViewport]);

  const centerOnSelectedNode = () => {
    const selectedId = visibleNodes.find(node => node.state === 'active')?.id;
    const position = selectedId ? positions[selectedId] : undefined;
    if (!position) {
      return;
    }

    setViewport(current => ({
      scale: current.scale,
      x: size.width / 2 - position.x * current.scale,
      y: size.height / 2 - position.y * current.scale,
    }));
  };

  const commonButtonProps: ButtonProps = {
    className: styles.zoomButton,
    size: 'small',
    type: 'button',
    variant: 'tertiary',
  };
  const isGraphEmpty = visibleNodes.length === 0;

  return (
    <div {...divProps} className={clsx(styles.root, className)} ref={rootRef}>
      <svg
        ref={svgRef}
        aria-hidden={ariaLabel ? undefined : true}
        aria-label={ariaLabel}
        className={styles.canvas}
        data-panning={isPanning || undefined}
        data-testid="network-graph"
        role={ariaLabel ? 'img' : undefined}
        viewBox={`0 0 ${size.width} ${size.height}`}
        {...panZoomHandlers}
      >
        <g ref={viewportGroupRef}>
          <NetworkGraphViewport
            connectedPositions={connectedPositions}
            expandedNodeIds={expandedNodeIds}
            externalHoveredNodeId={hoveredNodeId}
            labelOffsets={labelOffsets}
            rayAngles={rayAngles}
            laneOffsets={laneOffsets}
            nodeById={nodeById}
            nodes={visibleNodes}
            onEdgeClick={onEdgeClick}
            onEdgeHover={onEdgeHover}
            onNodeClick={onNodeClick}
            onNodeHover={onNodeHover}
            positions={positions}
            primaryNodeId={primaryNode?.id}
            renderableEdges={renderableEdges}
            renderEdgeTooltip={renderEdgeTooltip}
            suppressAfterDrag={suppressAfterDrag}
            targetAngleOffsets={targetAngleOffsets}
            visibleEdges={visibleEdges}
          />
        </g>
      </svg>
      <div className={styles.zoomControls}>
        <Tooltip
          disabled={isGraphEmpty}
          label={resolvedLocales.centerOnSelected}
          placement="right"
          trigger={TooltipTrigger.Hover}
          inPortal
        >
          <Button
            aria-label={resolvedLocales.centerOnSelected}
            disabled={isGraphEmpty}
            iconName="Exposure"
            onClick={centerOnSelectedNode}
            {...commonButtonProps}
          />
        </Tooltip>
        <Button
          aria-label={resolvedLocales.zoomIn}
          iconName="Add"
          onClick={() => zoomAtCenter(1.2)}
          {...commonButtonProps}
        />
        <Button
          aria-label={resolvedLocales.zoomOut}
          iconName="Minus"
          onClick={() => zoomAtCenter(1 / 1.2)}
          {...commonButtonProps}
        />
      </div>
    </div>
  );
};
